import { DEFAULT_MAP_SOURCE } from '@env';
import { isFunction } from 'lodash';

import { Component, EventEmitter, Inject, Input, Optional, Output } from '@angular/core';

import { IFsMap } from '@lib/geo/core';
import { IEsriMapOptions } from '@lib/geo/esri';
import {
  DEFAULT_MAP_OPTIONS,
  GoogleMapStyle,
  IGoogleMapOptions,
  MapDefaultOptions,
} from '@lib/geo/google/map';
import { SnLonLat, SnLonLatBounds, SnLonLatLike } from '@lib/geo/helpers';

import { MapFactoryService } from '../../services';

@Component({
  selector: 'fs-map',
  templateUrl: './map.component.html',
  styleUrls: ['./map.component.scss'],
})
export class FsMapComponent {
  public get mapSource(): string {
    return DEFAULT_MAP_SOURCE;
  }

  @Input()
  public center: SnLonLatLike;

  @Input()
  public zoom: number;

  @Input()
  public tilt: number;

  @Input()
  public options: IGoogleMapOptions | IEsriMapOptions = {};

  @Input()
  public isDeleting: boolean = false;

  @Input()
  public mapStyle: string;

  @Output()
  public readonly mapInit = new EventEmitter<IFsMap>();

  @Output()
  public readonly mapClick = new EventEmitter<SnLonLat>();

  @Output()
  public readonly mapDblclick = new EventEmitter<SnLonLat>();

  @Output()
  public readonly mapDragStart = new EventEmitter<void>();

  @Output()
  public readonly mapDrag = new EventEmitter<void>();

  @Output()
  public readonly mapDragEnd = new EventEmitter<void>();

  @Output()
  public readonly centerChange = new EventEmitter<SnLonLat>();

  @Output()
  public readonly boundsChange = new EventEmitter<SnLonLatBounds>();

  @Output()
  public readonly zoomChange = new EventEmitter<number>();

  @Output()
  public readonly styleChange = new EventEmitter<GoogleMapStyle>();

  public readonly defaultMapSource = DEFAULT_MAP_SOURCE.toString();

  private _mapInstance: IFsMap;

  constructor(
    @Inject(DEFAULT_MAP_OPTIONS) @Optional() defaultOptions: MapDefaultOptions,
    private readonly mapFactory: MapFactoryService,
  ) {
    const options = isFunction(defaultOptions) ? defaultOptions() : defaultOptions;

    this.options = { ...options, ...this.options };
    this.center = this.center || this.options.center;
    this.zoom = this.zoom || this.options.zoom;
  }

  public onMapInit(map: IFsMap): void {
    this._mapInstance = map;

    if (this.mapFactory.isGoogleMap(map)) {
      map.setOptions(this.options as IGoogleMapOptions);
    } else if (this.mapFactory.isEsriMap(map)) {
      map.setOptions({ ...this.options, animated: false } as IEsriMapOptions);
    }

    this.mapInit.emit(this._mapInstance);
    this.mapInit.complete();
  }

  public onMapClick(lonLat: SnLonLat): void {
    this.mapClick.emit(lonLat);
    this.mapClick.complete();
  }

  public onMapDblclick(lonLat: SnLonLat): void {
    this.mapDblclick.emit(lonLat);
    this.mapDblclick.complete();
  }

  public onMapDragStart(): void {
    this.mapDragStart.emit();
    this.mapDragStart.complete();
  }

  public onMapDrag(): void {
    this.mapDrag.emit();
    this.mapDblclick.complete();
  }

  public onMapDragEnd(): void {
    this.mapDragEnd.emit();
    this.mapDragEnd.complete();
  }

  public onCenterChange(lonLat: SnLonLat): void {
    this.centerChange.emit(lonLat);
    this.centerChange.complete();
  }

  public onBoundsChange(bounds: SnLonLatBounds): void {
    this.boundsChange.emit(bounds);
    this.boundsChange.complete();
  }

  public onZoomChange(zoom: number): void {
    this.zoomChange.emit(zoom);
    this.zoomChange.complete();
  }

  public onStyleChange(mapStyle: GoogleMapStyle): void {
    this.styleChange.emit(mapStyle);
    this.styleChange.complete();
  }
}
